import { getBearingDifference } from './NavigationHelpers';
import { supportsManeuver, opposesManeuver, isVehicleSignal } from '../../trafficInfra/SignalTypes';

/**
 * Match a signal from the topology based on user's location, bearing, and route maneuver.
 * 
 * @param {Object} topology - The full topology JSON object for the intersection
 * @param {number} userBearing - Current bearing of the user (degrees)
 * @param {string} routeManeuver - Expected maneuver ('STRAIGHT', 'LEFT', 'RIGHT', 'U-TURN')
 * @returns {Object|null} - The matched signal object (from topology.signals) or null
 */
export const matchSignal = (topology, userBearing, routeManeuver) => {
    if (!topology || !topology.signals) {
        console.warn("[SignalMatcher] Topology or signals missing", topology);
        return null;
    }

    const signalsList = Object.values(topology.signals);
    const BEARING_TOLERANCE = 45; // Degrees
    const OPPOSITE_THRESHOLD = 180 - BEARING_TOLERANCE; // 135 degrees

    let bestMatch = null;
    let bestMatchScore = -Infinity; // Higher is better

    // Evaluate each signal individually
    signalsList.forEach(signal => {
        if (!signal.label) return;

        // Skip non-vehicle signals (trams, pedestrians) from matching routing maneuvers
        if (!isVehicleSignal(signal.type)) return;

        const diff = Math.abs(getBearingDifference(userBearing, signal.bearing));

        // Check if the signal is facing opposite to us (diff >= 135)
        if (diff >= OPPOSITE_THRESHOLD) {
            const deviation = 180 - diff; // 0 deviation is perfect opposition
            let score = 0;

            const supports = supportsManeuver(signal.type, routeManeuver);
            if (supports) score += 10;

            const opposes = opposesManeuver(signal.type, routeManeuver);
            if (opposes) score -= 20;

            // Penalize deviation from perfect opposition
            score -= (deviation / 45) * 5;

            if (score > bestMatchScore) {
                bestMatchScore = score;
                bestMatch = { signal, deviation };
            }
        }
    });

    if (bestMatch) {
        // Group Key e.g "A1" -> "A"
        const match = bestMatch.signal.label.match(/^([A-Za-z]+)/);
        const groupKey = match ? match[1] : 'UNKNOWN';

        return {
            label: groupKey,
            full_label: bestMatch.signal.label,
            type: bestMatch.signal.type,
            bearing: bestMatch.signal.bearing,
            isExactMatch: !!routeManeuver
        };
    }

    return null;
};
